/**
 * Display helpers for canonical final letter packages from preview-generation-plan.
 * Letter counts come from packages.length — never from raw method groups.
 */

const BUREAU_ORDER = ['Equifax', 'Experian', 'TransUnion'];

export function normalizeFinalPackageFamily(pkg = {}) {
  const raw = String(
    pkg.package_family
    || pkg.resolved_family
    || pkg.family
    || pkg.runtime_method
    || pkg.package_method
    || pkg.resolved_method
    || ''
  ).trim().toLowerCase();
  return raw.replace(/\s+/g, '_');
}

/**
 * Human label for a canonical package. Combo always shows as
 * "Structural + Recovery Combo" regardless of stale method titles.
 */
export function getFinalPackageDisplayLabel(pkg = {}) {
  const family = normalizeFinalPackageFamily(pkg);
  const method = String(
    pkg.runtime_method || pkg.package_method || pkg.resolved_method || pkg.managed_method || ''
  ).toLowerCase();

  if (
    family === 'structural_recovery_combo'
    || family === 'structural_recovery'
    || method === 'structural_recovery_combo'
    || method.includes('structural_recovery')
  ) {
    return 'Structural + Recovery Combo';
  }
  if (family === 'recovery' || method === 'debt_validation' || method.includes('recovery')) {
    return 'Recovery';
  }
  if (family === 'structural' || method === 'structural_method') {
    return 'Structural';
  }
  if (family === 'isolated' || method === 'isolated_method' || method === 'factual_inaccuracy') {
    return 'Isolated';
  }
  if (family === 'data_breach' || method.includes('data_breach')) {
    return 'Data Breach';
  }
  if (pkg.package_label) {
    // Prefer bureau-less label when package_label is "X - Bureau"
    const label = String(pkg.package_label);
    const dash = label.lastIndexOf(' - ');
    if (dash > 0) return label.slice(0, dash);
    return label;
  }
  return method ? method.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Package';
}

export function formatFinalPackageTitle(pkg = {}) {
  const bureau = pkg.bureau || 'Unknown';
  return `${bureau} — ${getFinalPackageDisplayLabel(pkg)}`;
}

function resolveItemIds(pkg = {}) {
  if (Array.isArray(pkg.item_ids) && pkg.item_ids.length) {
    return pkg.item_ids.map(String);
  }
  if (Array.isArray(pkg.items) && pkg.items.length) {
    return pkg.items
      .map((item) => {
        if (typeof item === 'string' || typeof item === 'number') return String(item);
        return String(item?.item_id || item?.id || '');
      })
      .filter(Boolean);
  }
  const sections = pkg.section_groups || {};
  const fromSections = Object.values(sections).flatMap((ids) =>
    Array.isArray(ids) ? ids.map(String) : []
  );
  return [...new Set(fromSections)];
}

/**
 * Build ordered display rows from preview-generation-plan `packages`.
 * One row per canonical package; never merges across bureaus.
 */
export function buildFinalLetterPackagesFromPreview(packages = [], options = {}) {
  const { getItemDisplayName } = options;
  const rows = (Array.isArray(packages) ? packages : []).map((pkg, index) => {
    const itemIds = resolveItemIds(pkg);
    const family = normalizeFinalPackageFamily(pkg);
    const displayLabel = getFinalPackageDisplayLabel(pkg);
    const itemNames = typeof getItemDisplayName === 'function'
      ? itemIds.map((id) => getItemDisplayName(id)).filter(Boolean)
      : [];

    return {
      key: pkg.package_id || `${pkg.bureau || 'bureau'}_${family}_${index}`,
      package_id: pkg.package_id || null,
      bureau: pkg.bureau || '',
      package_family: family,
      package_label: pkg.package_label || formatFinalPackageTitle(pkg),
      display_label: displayLabel,
      title: formatFinalPackageTitle(pkg),
      item_count: Number(pkg.item_count) || itemIds.length,
      item_ids: itemIds,
      item_names: itemNames,
      section_groups: pkg.section_groups || {},
      runtime_method: pkg.runtime_method || pkg.package_method || pkg.resolved_method || null,
      managed_method: pkg.managed_method || null,
      source: pkg,
    };
  });

  return rows.sort((a, b) => {
    const ai = BUREAU_ORDER.indexOf(a.bureau);
    const bi = BUREAU_ORDER.indexOf(b.bureau);
    const aRank = ai === -1 ? 99 : ai;
    const bRank = bi === -1 ? 99 : bi;
    if (aRank !== bRank) return aRank - bRank;
    return String(a.package_id || '').localeCompare(String(b.package_id || ''));
  });
}

export function getFinalLetterCount(packages = []) {
  return Array.isArray(packages) ? packages.length : 0;
}
